'use client';

import { useState, useEffect } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface Signal {
  id: string;
  name: string;
  nameEn: string;
  value: number;
  weight: number;
  trend: 'up' | 'down' | 'stable';
  description: string;
  icon: string;
  color: string;
  unit: string;
}

interface TeamSignal {
  team: string;
  abbr: string;
  fatigue: number;
  travel: number;
  goaliePressure: number;
  chemistry: number;
  momentum: number;
  emotional: number;
  clutch: number;
  proprietary: number;
}

const TEAM_SIGNALS: TeamSignal[] = [
  { team: 'Boston Bruins', abbr: 'BOS', fatigue: 72, travel: 45, goaliePressure: 88, chemistry: 91, momentum: 78, emotional: 34, clutch: 85, proprietary: 82 },
  { team: 'Toronto Maple Leafs', abbr: 'TOR', fatigue: 58, travel: 62, goaliePressure: 71, chemistry: 84, momentum: 65, emotional: 67, clutch: 72, proprietary: 71 },
  { team: 'Tampa Bay Lightning', abbr: 'TBL', fatigue: 81, travel: 78, goaliePressure: 65, chemistry: 88, momentum: 54, emotional: 45, clutch: 79, proprietary: 68 },
  { team: 'Colorado Avalanche', abbr: 'COL', fatigue: 44, travel: 35, goaliePressure: 92, chemistry: 95, momentum: 89, emotional: 28, clutch: 91, proprietary: 91 },
  { team: 'Vegas Golden Knights', abbr: 'VGK', fatigue: 67, travel: 55, goaliePressure: 78, chemistry: 87, momentum: 71, emotional: 52, clutch: 83, proprietary: 79 },
];

const SIGNAL_HISTORY = Array.from({ length: 14 }, (_, i) => ({
  day: `J-${14 - i}`,
  fatigue: 50 + Math.sin(i * 0.5) * 20 + Math.random() * 10,
  travel: 40 + Math.cos(i * 0.4) * 15 + Math.random() * 8,
  momentum: 60 + Math.sin(i * 0.3) * 25 + Math.random() * 12,
  proprietary: 65 + Math.sin(i * 0.2) * 15 + Math.random() * 8,
}));

export default function ProprietarySignalClient() {
  const [selectedTeam, setSelectedTeam] = useState<TeamSignal>(TEAM_SIGNALS[3]);
  const [liveProprietaryScore, setLiveProprietaryScore] = useState(91);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveProprietaryScore(prev => Math.max(0, Math.min(100, prev + (Math.random() - 0.5) * 2)));
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const signals: Signal[] = [
    { id: 'fatigue', name: 'Fatigue Algorithmique', nameEn: 'Algorithmic Fatigue', value: selectedTeam.fatigue, weight: 18, trend: selectedTeam.fatigue > 70 ? 'up' : 'stable', description: 'Charge physique cumulée sur 7 jours', icon: 'BoltIcon', color: '#ef4444', unit: '/100' },
    { id: 'travel', name: 'Travel Stress Index', nameEn: 'Travel Stress', value: selectedTeam.travel, weight: 12, trend: selectedTeam.travel > 60 ? 'up' : 'down', description: 'Impact des déplacements sur performance', icon: 'MapPinIcon', color: '#f59e0b', unit: '/100' },
    { id: 'goalie', name: 'Goalie Pressure Score', nameEn: 'Goalie Pressure', value: selectedTeam.goaliePressure, weight: 22, trend: 'stable', description: 'Pression contextuelle sur le gardien', icon: 'ShieldCheckIcon', color: '#3b82f6', unit: '/100' },
    { id: 'chemistry', name: 'Lineup Chemistry Index', nameEn: 'Line Chemistry', value: selectedTeam.chemistry, weight: 16, trend: 'up', description: 'Synergie des lignes d\'attaque', icon: 'UsersIcon', color: '#10b981', unit: '/100' },
    { id: 'momentum', name: 'Momentum Persistence', nameEn: 'Momentum', value: selectedTeam.momentum, weight: 15, trend: selectedTeam.momentum > 70 ? 'up' : 'down', description: 'Persistance du momentum récent', icon: 'ArrowTrendingUpIcon', color: '#a855f7', unit: '/100' },
    { id: 'emotional', name: 'Emotional Volatility', nameEn: 'Emotional Vol.', value: selectedTeam.emotional, weight: 9, trend: selectedTeam.emotional > 50 ? 'up' : 'stable', description: 'Volatilité émotionnelle de l\'équipe', icon: 'ExclamationTriangleIcon', color: '#f97316', unit: '/100' },
    { id: 'clutch', name: 'Clutch Performance Index', nameEn: 'Clutch', value: selectedTeam.clutch, weight: 8, trend: 'stable', description: 'Performance dans les moments décisifs', icon: 'TrophyIcon', color: '#06b6d4', unit: '/100' },
  ];

  const radarData = signals.map(s => ({ metric: s.nameEn, value: s.value }));

  const proprietaryScore = Math.round(
    signals.reduce((acc, s) => acc + (s.value * s.weight / 100), 0)
  );

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="LockClosedIcon" size={20} className="text-yellow-400" />
            <h1 className="text-xl font-bold text-foreground">Proprietary Signal Engine</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-yellow-400/10 text-yellow-400 border border-yellow-400/20 rounded">PHASE QUANT · ÉTAPE 3</span>
          </div>
          <p className="text-sm text-muted-foreground">Signaux propriétaires impossibles à obtenir via APIs standard</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">PROPRIETARY SCORE</div>
            <div className="text-3xl font-bold text-yellow-400 font-mono">{proprietaryScore}</div>
          </div>
          <div className="w-16 h-16 rounded-full border-4 border-yellow-400/30 flex items-center justify-center relative">
            <div className="text-lg font-bold text-yellow-400 font-mono">{proprietaryScore}</div>
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="28" fill="none" stroke="#1e293b" strokeWidth="4" />
              <circle cx="32" cy="32" r="28" fill="none" stroke="#f59e0b" strokeWidth="4"
                strokeDasharray={`${(proprietaryScore / 100) * 175.9} 175.9`} strokeLinecap="round" />
            </svg>
          </div>
        </div>
      </div>

      {/* Team Selector */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TEAM_SIGNALS.map(team => (
          <button
            key={team.abbr}
            onClick={() => setSelectedTeam(team)}
            className={`flex-shrink-0 px-4 py-2 rounded-lg border text-xs font-medium transition-all ${selectedTeam.abbr === team.abbr ? 'bg-yellow-400/20 border-yellow-400 text-yellow-400' : 'border-border text-muted-foreground hover:border-yellow-400/50'}`}
          >
            <span className="font-mono font-bold">{team.abbr}</span>
            <span className="ml-2 text-2xs opacity-70">Score: {team.proprietary}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Signal Cards */}
        <div className="col-span-2 grid grid-cols-2 gap-3">
          {signals.map(signal => (
            <div key={signal.id} className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon name={signal.icon as Parameters<typeof Icon>[0]['name']} size={14} style={{ color: signal.color }} />
                  <span className="text-xs font-medium text-foreground">{signal.name}</span>
                </div>
                <span className={`text-2xs ${signal.trend === 'up' ? 'text-green-400' : signal.trend === 'down' ? 'text-red-400' : 'text-muted-foreground'}`}>
                  {signal.trend === 'up' ? '↑' : signal.trend === 'down' ? '↓' : '→'}
                </span>
              </div>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-2xl font-bold font-mono" style={{ color: signal.color }}>{signal.value}</span>
                <span className="text-xs text-muted-foreground mb-0.5">{signal.unit}</span>
                <span className="text-2xs text-muted-foreground mb-0.5 ml-auto">Poids: {signal.weight}%</span>
              </div>
              <div className="h-1.5 bg-border rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${signal.value}%`, backgroundColor: signal.color }} />
              </div>
              <p className="text-2xs text-muted-foreground mt-1.5">{signal.description}</p>
            </div>
          ))}
        </div>

        {/* Radar */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">{selectedTeam.team}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#334155" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 9 }} />
              <Radar dataKey="value" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-3 p-3 bg-background rounded border border-yellow-400/20">
            <div className="text-2xs text-yellow-400 font-mono mb-1">PROPRIETARY COMPOSITE</div>
            <div className="text-3xl font-bold text-yellow-400 font-mono text-center">{proprietaryScore}</div>
            <div className="text-2xs text-muted-foreground text-center mt-1">Score combiné pondéré</div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Comparaison Équipes — Signaux Propriétaires</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-2.5 text-left text-2xs font-medium text-muted-foreground uppercase">Équipe</th>
                {signals.map(s => <th key={s.id} className="px-3 py-2.5 text-center text-2xs font-medium text-muted-foreground uppercase">{s.nameEn}</th>)}
                <th className="px-4 py-2.5 text-center text-2xs font-medium text-yellow-400 uppercase">Score</th>
              </tr>
            </thead>
            <tbody>
              {TEAM_SIGNALS.map(team => {
                const ts = [team.fatigue, team.travel, team.goaliePressure, team.chemistry, team.momentum, team.emotional, team.clutch];
                return (
                  <tr key={team.abbr} className={`border-b border-border/50 hover:bg-background/30 transition-colors ${selectedTeam.abbr === team.abbr ? 'bg-yellow-400/5' : ''}`}>
                    <td className="px-4 py-2.5 font-medium text-foreground">{team.abbr}</td>
                    {ts.map((val, i) => (
                      <td key={i} className="px-3 py-2.5 text-center">
                        <span className={`font-mono text-xs ${val >= 80 ? 'text-green-400' : val >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>{val}</span>
                      </td>
                    ))}
                    <td className="px-4 py-2.5 text-center">
                      <span className="font-mono font-bold text-yellow-400">{team.proprietary}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
